import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { checkExistingCollections, createFundPayments } from 'lib/api/funds';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate, showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import EditArrearDialog from '../../../../Payments/components/PayIns/components/Conciliation/Conciliate/EditArrearDialog';
import { ResellWithSuggestions, useFactoringResell } from '../../context';
import CollectionsWarningDialog from './components/CollectionsWarningDialog';

type EditArrearDocument = {
  documentId: string;
  arrearStructure: {
    arrearType: string;
    arrearRate: number;
    arrearSegments: {
      min: number;
      max: number;
      value: number;
    }[];
  };
  hasConciliations: boolean;
} | null;

const MultiPaymentFundResell: React.FC = () => {
  const {
    openMultiPayment,
    setOpenMultiPayment,
    resellsSelected,
    setResellsSelected,
    setSelectedResellIds,
    fetchResellings,
    pagination,
  } = useFactoringResell();
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [openConfirmMultiPayment, setOpenConfirmMultiPayment] = useState(false);
  const newOperationDate = dayjs().hour(12).minute(0).second(0).millisecond(0);
  const [paymentDate, setPaymentDate] = useState<Dayjs>(newOperationDate);
  const [editArrearDocument, setEditArrearDocument] =
    useState<EditArrearDocument>(null);
  const [openEditArrearDialog, setOpenEditArrearDialog] = useState(false);
  const [openCollectionsWarning, setOpenCollectionsWarning] = useState(false);
  const [documentsWithWarnings, setDocumentsWithWarnings] = useState<
    {
      documentId: string;
      folio: string;
      businessName: string;
      resellId: number;
      resellNetAmount: number;
      totalConciliated: number;
      pendingAmount: number;
      proposedPayment: number;
      hasExistingCollections: boolean;
      wouldCreateExcess: boolean;
      excessAmount: number;
      conciliationsSummary: {
        fundPayments: number;
        ownCollections: number;
        resellNominees: number;
        totalConciliations: number;
      };
    }[]
  >([]);
  const [validationLoading, setValidationLoading] = useState(false);

  const validParams = () => {
    if (!paymentDate) {
      showAlert(dispatch, 'warning', 'Debes seleccionar una fecha');
      return false;
    }
    const allPaymentsHaveAmount = resellsSelected.every(
      (resell: { suggestedAmountPayment: number }) =>
        resell.suggestedAmountPayment,
    );
    if (!allPaymentsHaveAmount) {
      showAlert(dispatch, 'warning', 'Debes completar el monto a registrar');
      return false;
    }
    return true;
  };

  const handleCheckCollections = async () => {
    if (!validParams()) {
      return;
    }

    setValidationLoading(true);
    setOpenConfirmMultiPayment(false);

    try {
      const paymentsToCheck = resellsSelected.map(
        (resell: ResellWithSuggestions) => ({
          factoringResellId: resell.id,
          amount: resell.suggestedAmountPayment,
          documentId: resell.documentId,
        }),
      );

      const result = await checkExistingCollections(dispatch, {
        payments: paymentsToCheck,
      });

      setValidationLoading(false);

      if (result.hasWarnings) {
        const docsWithWarnings = result.documentsWithCollections.filter(
          (doc: {
            hasExistingCollections: boolean;
            wouldCreateExcess: boolean;
          }) => doc.hasExistingCollections || doc.wouldCreateExcess,
        );
        setDocumentsWithWarnings(docsWithWarnings);
        setOpenCollectionsWarning(true);
      } else {
        await handleSubmit();
      }
    } catch (error) {
      setValidationLoading(false);
      showAlert(dispatch, 'error', 'Error al validar los pagos');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setOpenCollectionsWarning(false);
    try {
      const paymentsWithDate = resellsSelected.map(
        (resell: {
          id: number;
          suggestedAmountPayment: number;
          fundId: string;
          businessIdentifier: string;
          documentId: string;
        }) => ({
          factoringResellId: resell.id,
          amount: resell.suggestedAmountPayment,
          fundId: resell.fundId,
          businessIdentifier: resell.businessIdentifier,
          documentId: resell.documentId,
          date: paymentDate,
        }),
      );
      const { surplusWarnings } = await createFundPayments(dispatch, {
        payments: paymentsWithDate,
      });
      void fetchResellings({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setOpenMultiPayment(false);
      setSelectedResellIds([]);
      if (
        surplusWarnings &&
        Array.isArray(surplusWarnings) &&
        surplusWarnings.length > 0
      ) {
        showAlert(
          dispatch,
          'warning',
          'Pago registrado correctamente. Hubo un error al crear el excedente de la venta.',
        );
      } else {
        showAlert(dispatch, 'success', 'Pagos registrados correctamente');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showAlert(
        dispatch,
        'error',
        err.response?.data?.message || 'Error al registrar los pagos',
      );
    }
    setLoading(false);
  };

  const handleChangeAmountToConciliate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    const resell = resellsSelected.find(
      (resell: ResellWithSuggestions) =>
        String(resell.id) === event.target.name,
    );
    if (!resell) return;
    resell.suggestedAmountPayment = Number(amount);
    setResellsSelected([...resellsSelected]);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      const dateWithFixedHour = date
        .hour(12)
        .minute(0)
        .second(0)
        .millisecond(0);
      setPaymentDate(dateWithFixedHour);
    } else {
      setPaymentDate(dayjs().hour(12).minute(0).second(0).millisecond(0));
    }
  };

  const DateSelector = () => {
    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
          <DatePicker
            label="Fecha"
            value={paymentDate}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />
        </LocalizationProvider>
      </div>
    );
  };

  const handleEditArrearStructure = (resellId: number) => {
    const resell = resellsSelected.find(
      (resell: ResellWithSuggestions) => resell.id === resellId,
    );
    if (!resell) return showAlert(dispatch, 'error', 'Pago no encontrado');
    const disabled =
      resell.conciliationLog.length > 0
        ? new Date(
            resell.conciliationLog[resell.conciliationLog.length - 1].date,
          ) >= paymentDate.toDate()
        : false;
    if (disabled)
      return showAlert(
        dispatch,
        'error',
        'No se puede editar la mora de este documento porque tiene conciliaciones futuras',
      );
    setEditArrearDocument({
      documentId: resell.documentId,
      arrearStructure: resell.documentArrearStructure,
      hasConciliations: resell.conciliationLog.length > 0,
    });
    setOpenEditArrearDialog(true);
  };

  const handleSaveArrearStructure = () => {
    setEditArrearDocument(null);
    setOpenEditArrearDialog(false);
    setResellsSelected([]);
    setSelectedResellIds([]);
    void fetchResellings({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    setOpenMultiPayment(false);
  };

  return (
    <>
      <Dialog maxWidth="xl" fullWidth open={openMultiPayment}>
        <div style={{ padding: 20, minHeight: 200 }}>
          {loading || validationLoading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <div>
              <HeaderWithActions
                title="Registrar pagos"
                item={DateSelector()}
              />
              <Typography variant="body1"></Typography>
              <div style={{ padding: '10px 0px' }}>
                <EnhancedTable
                  data={resellsSelected}
                  dataHeaders={[
                    {
                      key: 'id',
                      label: 'Id',
                      type: 'numeric-id',
                    },
                    {
                      key: 'folio',
                      label: 'Folio',
                    },
                    {
                      key: 'businessName',
                      label: 'Cliente',
                    },
                    {
                      key: 'stakeholderName',
                      label: 'Deudor',
                    },
                    {
                      key: 'fundName',
                      label: 'Fondo',
                    },
                    {
                      key: 'dueDate',
                      label: 'Fecha vencimiento fondo',
                      type: 'date',
                      formatter: (value: Date) =>
                        formatDate(value, 'dd-mm-yyyy'),
                    },
                    {
                      key: 'paymentDate',
                      label: 'Fecha vencimiento doc.',
                      type: 'date',
                      formatter: (value: Date) =>
                        formatDate(value, 'dd-mm-yyyy'),
                    },
                    {
                      key: 'amount',
                      label: 'Monto Documento',
                      type: 'money',
                    },
                    {
                      key: 'documentAmountConciliated',
                      label: 'Monto conciliado documento fondo',
                      type: 'money',
                    },
                    {
                      key: 'documentArrearStructure',
                      label: 'Tasa de mora doc. cliente',
                      type: 'arrear-structure-editable',
                      customAction: handleEditArrearStructure,
                      extraData: {
                        operationDate: paymentDate.toDate(),
                        user,
                      },
                    },
                    {
                      key: 'suggestedAmountPayment',
                      label: 'Monto a registrar',
                      type: 'money',
                      handleChange: handleChangeAmountToConciliate,
                    },
                  ]}
                />
              </div>
              <DialogActions>
                <CustomButton
                  onClick={() => setOpenMultiPayment(false)}
                  color="secondary"
                >
                  Salir
                </CustomButton>
                <CustomButton
                  onClick={() => setOpenConfirmMultiPayment(true)}
                  color="primary"
                >
                  Marcar pagos
                </CustomButton>
              </DialogActions>
            </div>
          )}
        </div>
        <Dialog
          open={openConfirmMultiPayment}
          onClose={() => setOpenConfirmMultiPayment(false)}
        >
          <DialogTitle>
            <Typography variant="h6">Confirmar pagos</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Estás seguro de querer registrar los avisos de pago desde los
              fondos?
            </Typography>
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpenConfirmMultiPayment(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={() => handleCheckCollections()}
              color="primary"
            >
              Confirmar
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Dialog>
      {openCollectionsWarning && (
        <CollectionsWarningDialog
          open={openCollectionsWarning}
          onClose={() => setOpenCollectionsWarning(false)}
          onConfirm={() => void handleSubmit()}
          documentsWithWarnings={documentsWithWarnings}
        />
      )}
      {openEditArrearDialog && (
        <EditArrearDialog
          open={openEditArrearDialog}
          setOpen={setOpenEditArrearDialog}
          document={editArrearDocument}
          onSave={handleSaveArrearStructure}
          conciliationDate={new Date(paymentDate.toDate())}
        />
      )}
    </>
  );
};

export default MultiPaymentFundResell;
